import React, { useMemo, useState } from 'react';
import type { MouseEvent } from 'react';
import {
  Box,
  Button,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  type MenuProps,
} from '@mui/material';
import {
  CloudUploadOutlined as CloudUploadOutlinedIcon,
  Close as CloseIcon,
  DeleteOutlined as DeleteOutlineIcon,
  DownloadOutlined as DownloadOutlinedIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  KeyOffOutlined as KeyOffOutlinedIcon,
  KeyOutlined as KeyOutlinedIcon,
  VisibilityOutlined as VisibilityOutlinedIcon,
} from '@mui/icons-material';
import type { TApiApplicationDocument } from '~/modules/covenants/types.ts';
import { useDocListContext } from '~/modules/covenants/CovenantsDataApplicationForm/components/DocListContext.tsx';
import { appFormStyles } from '~/modules/covenants/CovenantsDataApplicationForm/styles.ts';
import {
  DELETE_SIGNATURE_LABEL,
  DOC_ACTION_LABELS,
  DOC_ACTION_MENU_ID_PREFIXES,
  DOC_ACTION_MENU_ORIGIN,
  TRANSPARENT_MENU_BACKDROP_STYLES,
} from '~/modules/covenants/CovenantsDataApplicationForm/constants.ts';
import { useRequiredDocumentUploadAction } from '~/modules/covenants/CovenantsDataApplicationForm/hooks/useRequiredDocumentUploadAction.ts';
import { RequiredDocumentUploadConfirmModal } from '~/modules/covenants/CovenantsDataApplicationForm/components/RequiredDocumentUploadConfirmModal.tsx';
import {
  buildApplicationDocumentRowKey,
  canAttachSignature,
  canDeleteFile,
  canDeleteSignature,
} from '~/modules/covenants/utils.ts';
import { EMPTY_SIGNATURE_VALIDATION_DOCUMENT_STATE } from '~/modules/covenants/constants.tsx';
import { isFeatureOn } from '~/helpers/utils.ts';
import { isDocumentGenerationAvailable } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration.ts';
import { DocumentGenerationModal } from '~/modules/covenants/CovenantsDataApplicationForm/components/DocumentGenerationModal.tsx';
import type { TFinancialConditionFormValues } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/types.ts';
import type { TIncomeStructureByKvedFormValues } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/incomeStructureByKved/types.ts';

interface IDocActionsProps {
  mode: 'document' | 'file';
  doc: TApiApplicationDocument;
  groupHasFiles?: boolean;
  onOpenCovenants: () => void;
}

const actionMenuSlotProps: MenuProps['slotProps'] = {
  paper: { sx: appFormStyles.actionMenuPaper },
  backdrop: {
    invisible: true,
    sx: TRANSPARENT_MENU_BACKDROP_STYLES,
  },
};

export const DocActions: React.FC<IDocActionsProps> = ({
  mode,
  doc,
  groupHasFiles = false,
  onOpenCovenants,
}) => {
  const {
    isNewApplication,
    isReadOnly,
    localFiles,
    signatureValidationState: signatureValidationStateMap,
    onUploadForDoc,
    onUploadSignatureClick,
    onDeleteSignature,
    onPreviewDocument,
    onDownloadDocument,
    onDeleteDocument,
    onAddDocumentRow,
    onSaveFinancialCondition,
    onLoadFinancialCondition,
    onSaveIncomeStructureByKved,
    onLoadIncomeStructureByKved,
    onGenerateDocument,
  } = useDocListContext();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isDocumentGenerationModalOpen, setIsDocumentGenerationModalOpen] = useState(false);
  const [isGeneratingDocument, setIsGeneratingDocument] = useState(false);
  const isMenuOpen = Boolean(anchorEl);
  const stableKey = buildApplicationDocumentRowKey(doc);
  const localFile = localFiles[stableKey];
  const signatureValidationState =
    signatureValidationStateMap[String(doc.documentId)] ??
    EMPTY_SIGNATURE_VALIDATION_DOCUMENT_STATE;
  const hasFile = !!doc.fileName || !!localFile?.file;
  const canShowAttachSignature = canAttachSignature(doc, localFile, signatureValidationState);
  const hasAttachedSignature =
    !!localFile?.signature ||
    !!localFile?.isExternal ||
    doc.isCustomSignFile ||
    !!doc.hasSignedFile ||
    signatureValidationState.hasGeneratedProtocol;
  const canRemovePendingRow = !!doc.isVirtualRow && !hasFile;
  const shouldShowGenerateDocumentAction = isDocumentGenerationAvailable(
    doc.documentId,
    isNewApplication,
    isFeatureOn('VITE_COVENANTS_DOCUMENT_GENERATION_ENABLED')
  );
  const documentMenuId = `${DOC_ACTION_MENU_ID_PREFIXES.document}-${stableKey}`;
  const fileMenuId = `${DOC_ACTION_MENU_ID_PREFIXES.file}-${stableKey}`;

  const handlePreviewClick = () => {
    onPreviewDocument(doc, localFile);
  };

  const handleDownloadClick = () => {
    onDownloadDocument(doc, localFile);
  };

  const handleUploadClick = () => {
    if (mode === 'document' && groupHasFiles) {
      onAddDocumentRow(doc);
      return;
    }

    onUploadForDoc(stableKey);
  };

  const handleUploadSignatureClick = () => {
    onUploadSignatureClick(stableKey, doc);
  };

  const handleDeleteSignatureClick = () => {
    onDeleteSignature(stableKey, doc);
  };

  const handleDeleteDocumentClick = () => {
    onDeleteDocument(stableKey);
  };

  const {
    isConfirmModalOpen,
    requiredUploadConfig,
    confirmableStatus,
    onUploadClick,
    onCloseConfirmModal,
    onConfirmUpload,
  } = useRequiredDocumentUploadAction({
    doc,
    isNewApplication,
    onUpload: handleUploadClick,
  });

  const shouldShowRequiredUploadAction =
    !hasFile && !!requiredUploadConfig && !isReadOnly && !canRemovePendingRow;
  const shouldShowReplaceUploadAction =
    !!requiredUploadConfig &&
    !isReadOnly &&
    !canRemovePendingRow &&
    !canShowAttachSignature &&
    !hasAttachedSignature;

  const handleMenuOpen = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setAnchorEl((currentAnchor) => (currentAnchor ? null : event.currentTarget));
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleOpenDocumentGenerationModal = () => {
    setIsDocumentGenerationModalOpen(true);
  };

  const handleCloseDocumentGenerationModal = () => {
    if (isGeneratingDocument) {
      return;
    }

    setIsDocumentGenerationModalOpen(false);
  };

  const handleGenerateDocument = async (isFop?: boolean) => {
    setIsGeneratingDocument(true);

    try {
      const isGenerated = await onGenerateDocument(doc, isFop);

      if (isGenerated) {
        setIsDocumentGenerationModalOpen(false);
      }
    } finally {
      setIsGeneratingDocument(false);
    }
  };

  const handleSaveFinancialCondition = (values: TFinancialConditionFormValues) =>
    onSaveFinancialCondition(values);

  const handleLoadFinancialCondition = () => onLoadFinancialCondition(doc);

  const handleSaveIncomeStructureByKved = (values: TIncomeStructureByKvedFormValues) =>
    onSaveIncomeStructureByKved(doc, values);

  const handleLoadIncomeStructureByKved = () => onLoadIncomeStructureByKved(doc);

  const runMenuAction = (action: () => void) => {
    handleMenuClose();
    action();
  };

  const fileMenuActions = useMemo(
    () => ({
      showUpload: shouldShowRequiredUploadAction || shouldShowReplaceUploadAction,
      showAttachSignature: canShowAttachSignature,
      showDeleteSignature: canDeleteSignature(doc, localFile, signatureValidationState),
      showDeleteDocument: canDeleteFile(doc, localFile) || canRemovePendingRow,
      showPreview: hasFile,
      showDownload: hasFile,
    }),
    [
      canRemovePendingRow,
      canShowAttachSignature,
      doc,
      hasFile,
      localFile,
      shouldShowReplaceUploadAction,
      shouldShowRequiredUploadAction,
      signatureValidationState,
    ]
  );

  if (mode === 'document') {
    if (!isNewApplication) {
      return (
        <>
          <Button
            variant="outlined"
            size="small"
            onClick={(event) => {
              event.stopPropagation();
              onOpenCovenants();
            }}
            sx={appFormStyles.covenantActionButton}
          >
            {DOC_ACTION_LABELS.viewCovenant}
          </Button>

          <RequiredDocumentUploadConfirmModal
            open={isConfirmModalOpen}
            status={confirmableStatus}
            onConfirm={onConfirmUpload}
            onClose={onCloseConfirmModal}
          />
        </>
      );
    }

    return (
      <>
        <Button
          variant="outlined"
          size="small"
          onClick={handleMenuOpen}
          sx={appFormStyles.mainActionButton}
          endIcon={isMenuOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          aria-controls={isMenuOpen ? documentMenuId : undefined}
          aria-haspopup="true"
          aria-expanded={isMenuOpen ? 'true' : undefined}
        >
          {DOC_ACTION_LABELS.chooseAction}
        </Button>

        <Menu
          id={documentMenuId}
          anchorEl={anchorEl}
          open={isMenuOpen}
          onClose={handleMenuClose}
          disableScrollLock
          transformOrigin={DOC_ACTION_MENU_ORIGIN.transform}
          anchorOrigin={DOC_ACTION_MENU_ORIGIN.anchor}
          slotProps={actionMenuSlotProps}
        >
          <MenuItem onClick={() => runMenuAction(onUploadClick)} sx={appFormStyles.actionMenuItem}>
            <ListItemText primary={DOC_ACTION_LABELS.uploadDocument} />
          </MenuItem>
          <MenuItem
            onClick={() => runMenuAction(onOpenCovenants)}
            sx={appFormStyles.actionMenuItem}
          >
            <ListItemText primary={DOC_ACTION_LABELS.viewCovenant} />
          </MenuItem>
          {shouldShowGenerateDocumentAction && (
            <MenuItem
              onClick={() => runMenuAction(handleOpenDocumentGenerationModal)}
              sx={appFormStyles.actionMenuItem}
            >
              <ListItemText primary={DOC_ACTION_LABELS.generateDocument} />
            </MenuItem>
          )}
        </Menu>

        <DocumentGenerationModal
          open={isDocumentGenerationModalOpen}
          documentId={doc.documentId}
          documentName={doc.documentName}
          isGenerating={isGeneratingDocument}
          onSaveFinancialCondition={handleSaveFinancialCondition}
          onLoadFinancialCondition={handleLoadFinancialCondition}
          onSaveIncomeStructureByKved={handleSaveIncomeStructureByKved}
          onLoadIncomeStructureByKved={handleLoadIncomeStructureByKved}
          onGenerate={handleGenerateDocument}
          onClose={handleCloseDocumentGenerationModal}
        />

        <RequiredDocumentUploadConfirmModal
          open={isConfirmModalOpen}
          status={confirmableStatus}
          onConfirm={onConfirmUpload}
          onClose={onCloseConfirmModal}
        />
      </>
    );
  }

  if (
    !fileMenuActions.showUpload &&
    !fileMenuActions.showAttachSignature &&
    !fileMenuActions.showDeleteSignature &&
    !fileMenuActions.showDeleteDocument &&
    !fileMenuActions.showPreview &&
    !fileMenuActions.showDownload
  ) {
    return (
      <RequiredDocumentUploadConfirmModal
        open={isConfirmModalOpen}
        status={confirmableStatus}
        onConfirm={onConfirmUpload}
        onClose={onCloseConfirmModal}
      />
    );
  }

  return (
    <>
      <IconButton
        size="small"
        onClick={handleMenuOpen}
        sx={appFormStyles.fileActionMenuButton(isMenuOpen)}
        aria-label={DOC_ACTION_LABELS.fileActionsAria}
        aria-controls={isMenuOpen ? fileMenuId : undefined}
        aria-haspopup="true"
        aria-expanded={isMenuOpen ? 'true' : undefined}
      >
        {isMenuOpen ? (
          <CloseIcon fontSize="small" sx={appFormStyles.fileActionMenuIcon} />
        ) : (
          <Box component="span" sx={appFormStyles.fileActionMenuDots} aria-hidden="true">
            <Box component="span" sx={appFormStyles.fileActionMenuDot} />
            <Box component="span" sx={appFormStyles.fileActionMenuDot} />
            <Box component="span" sx={appFormStyles.fileActionMenuDot} />
          </Box>
        )}
      </IconButton>

      <Menu
        id={fileMenuId}
        anchorEl={anchorEl}
        open={isMenuOpen}
        onClose={handleMenuClose}
        disableScrollLock
        transformOrigin={DOC_ACTION_MENU_ORIGIN.transform}
        anchorOrigin={DOC_ACTION_MENU_ORIGIN.anchor}
        slotProps={actionMenuSlotProps}
      >
        {fileMenuActions.showUpload && (
          <MenuItem onClick={() => runMenuAction(onUploadClick)} sx={appFormStyles.actionMenuItem}>
            <ListItemText primary={DOC_ACTION_LABELS.uploadDocument} />
            <ListItemIcon sx={appFormStyles.actionMenuItemIcon}>
              <CloudUploadOutlinedIcon fontSize="small" />
            </ListItemIcon>
          </MenuItem>
        )}

        {fileMenuActions.showAttachSignature && (
          <MenuItem
            onClick={() => runMenuAction(handleUploadSignatureClick)}
            sx={appFormStyles.actionMenuItem}
          >
            <ListItemText primary={DOC_ACTION_LABELS.attachSignature} />
            <ListItemIcon sx={appFormStyles.actionMenuItemIcon}>
              <KeyOutlinedIcon fontSize="small" />
            </ListItemIcon>
          </MenuItem>
        )}

        {fileMenuActions.showPreview && (
          <MenuItem
            onClick={() => runMenuAction(handlePreviewClick)}
            sx={appFormStyles.actionMenuItem}
          >
            <ListItemText primary={DOC_ACTION_LABELS.previewDocument} />
            <ListItemIcon sx={appFormStyles.actionMenuItemIcon}>
              <VisibilityOutlinedIcon fontSize="small" />
            </ListItemIcon>
          </MenuItem>
        )}

        {fileMenuActions.showDownload && (
          <MenuItem
            onClick={() => runMenuAction(handleDownloadClick)}
            sx={appFormStyles.actionMenuItem}
          >
            <ListItemText primary={DOC_ACTION_LABELS.downloadDocument} />
            <ListItemIcon sx={appFormStyles.actionMenuItemIcon}>
              <DownloadOutlinedIcon fontSize="small" />
            </ListItemIcon>
          </MenuItem>
        )}

        {fileMenuActions.showDeleteSignature && (
          <MenuItem
            onClick={() => runMenuAction(handleDeleteSignatureClick)}
            sx={appFormStyles.actionMenuItem}
          >
            <ListItemText primary={DELETE_SIGNATURE_LABEL} />
            <ListItemIcon sx={appFormStyles.actionMenuItemIcon}>
              <KeyOffOutlinedIcon fontSize="small" />
            </ListItemIcon>
          </MenuItem>
        )}

        {fileMenuActions.showDeleteDocument && (
          <MenuItem
            onClick={() => runMenuAction(handleDeleteDocumentClick)}
            sx={appFormStyles.actionMenuItemDanger}
          >
            <ListItemText primary={DOC_ACTION_LABELS.deleteDocument} />
            <ListItemIcon sx={appFormStyles.actionMenuItemDangerIcon}>
              <DeleteOutlineIcon fontSize="small" />
            </ListItemIcon>
          </MenuItem>
        )}
      </Menu>

      <RequiredDocumentUploadConfirmModal
        open={isConfirmModalOpen}
        status={confirmableStatus}
        onConfirm={onConfirmUpload}
        onClose={onCloseConfirmModal}
      />
    </>
  );
};
